import {
  AdvanceCurriculumPhaseArgs,
  AdvanceCurriculumPhaseResult,
  CloneAtVersionArgs,
  CloneCurriculumArgs,
  CollabVersion,
  CreateCurriculumArgs,
  CreateDocumentResult,
  CurriculumLifecycleArgs,
  CurriculumLifecycleResult,
  type CurriculumPhase,
  CurriculumReworkResult,
  type CurriculumRole,
  type CurriculumTags,
  type DocumentId,
  OpenCurriculumReworkArgs,
  PublishCurriculumArgs,
  PublishCurriculumResult,
  SetCurriculumRoleArgs,
  SetCurriculumRoleResult,
  SetCurriculumTagsArgs,
  SetCurriculumTagsResult,
} from "@curriculum-studio/core";
import { HttpClient, HttpClientRequest, HttpClientResponse } from "@effect/platform";
import { Data, Effect, Schema } from "effect";

// The Expert/UI-only document-lifecycle half of the client (R-DO1, R-DO2, R-DO5): clone an existing
// curriculum and import a new one from uploaded markdown. Both POST through `@effect/platform`'s
// `HttpClient` and encode their bodies through the shared `core` boundary Schemas so branded ids go
// out exactly as the server decodes them; the `CreateDocumentResult` response is decoded the same
// way. There is no agent path to mirror — these are Expert affordances only.

// Any non-2xx response must surface as a *tagged* failure carrying the status (and the server's
// `{ message }` when present) so the UI can show the 404 (source deleted by another session) or 422
// (empty/markup/oversized) inline rather than letting a `CreateDocumentResult` decode fail as an
// opaque `ParseError`. Mirrors `ResolveRequestFailed` in `gate-actions.ts`.
export class CreateRequestFailed extends Data.TaggedError("CreateRequestFailed")<{
  readonly status: number;
  readonly message: string;
}> {}

// The 422/404 error bodies share the server's `{ kind, message }` envelope; decode `message`
// through a permissive boundary Schema (parse, don't validate) and fall back to undefined when the
// body is absent or unparseable.
const CreateErrorBody = Schema.Struct({ message: Schema.optional(Schema.String) });

const failureMessage = (response: HttpClientResponse.HttpClientResponse) =>
  HttpClientResponse.schemaBodyJson(CreateErrorBody)(response).pipe(
    Effect.map((body) => body.message),
    Effect.orElseSucceed(() => undefined),
  );

// The document-creating actions (clone, clone-at-version, import) share one wire shape: POST the
// decoded args, decode a `CreateDocumentResult`, and collapse any non-2xx into a tagged
// `CreateRequestFailed` the UI shows inline. Mirrors the `lifecyclePost` extraction below (which the
// archive lifecycle actions use); the args schema + payload are the only per-action difference.
const createPost = <A, I>(
  path: string,
  argsSchema: Schema.Schema<A, I>,
  payload: A,
  failure: string,
) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const request = yield* HttpClientRequest.post(path).pipe(
      HttpClientRequest.schemaBodyJson(argsSchema)(payload),
    );
    const response = yield* client.execute(request);
    if (response.status >= 400) {
      const message = yield* failureMessage(response);
      return yield* Effect.fail(
        new CreateRequestFailed({
          status: response.status,
          message: message ?? `${failure} (HTTP ${response.status}).`,
        }),
      );
    }
    return yield* HttpClientResponse.schemaBodyJson(CreateDocumentResult)(response);
  }).pipe(Effect.scoped);

export const cloneCurriculum = (sourceId: DocumentId) =>
  createPost(
    "/api/curricula/clone",
    CloneCurriculumArgs,
    { sourceId },
    "The curriculum could not be cloned",
  );

// Clone from a chosen historical version (Clone from history): same shape as `cloneCurriculum`, plus
// the `atVersion` (a plain number branded to `CollabVersion` at the wire boundary so callers — the
// History rail — pass `entry.version` without importing the brand).
export const cloneAtVersion = (sourceId: DocumentId, atVersion: number) =>
  createPost(
    "/api/curricula/clone-at-version",
    CloneAtVersionArgs,
    { sourceId, atVersion: CollabVersion.make(atVersion) },
    "The curriculum could not be cloned",
  );

// The archive lifecycle actions share one wire shape: POST `{ documentId }`, echo the id back.
// Non-2xx surfaces as the same tagged `CreateRequestFailed` the clone/import actions raise, so the
// index shows the 404 (already deleted elsewhere) or 409 (not archived any more) inline.
const lifecyclePost = (path: string, documentId: DocumentId, failure: string) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const request = yield* HttpClientRequest.post(path).pipe(
      HttpClientRequest.schemaBodyJson(CurriculumLifecycleArgs)({ documentId }),
    );
    const response = yield* client.execute(request);
    if (response.status >= 400) {
      const message = yield* failureMessage(response);
      return yield* Effect.fail(
        new CreateRequestFailed({
          status: response.status,
          message: message ?? `${failure} (HTTP ${response.status}).`,
        }),
      );
    }
    return yield* HttpClientResponse.schemaBodyJson(CurriculumLifecycleResult)(response);
  }).pipe(Effect.scoped);

export const deleteCurriculum = (documentId: DocumentId) =>
  lifecyclePost("/api/curricula/delete", documentId, "The curriculum could not be deleted");

// Posts to the unified `/api/curricula/create` endpoint's import branch (`_tag: "import"`) — see
// that endpoint's doc comment in `rest.ts` for why import and generate now share one wire shape.
export const importDocument = (
  text: string,
  tags: CurriculumTags,
  role?: CurriculumRole,
  phase?: CurriculumPhase,
) =>
  createPost(
    "/api/curricula/create",
    CreateCurriculumArgs,
    {
      _tag: "import",
      text,
      tags,
      ...(role !== undefined ? { role } : {}),
      ...(phase !== undefined ? { phase } : {}),
    },
    "The document could not be imported",
  );

// Set (or re-set) one curriculum's IDENTITY tags — level + language (the curriculum-tags ADR): the
// Expert's identity-edit affordance on the curriculum page. Same tagged-failure surface as the other
// lifecycle actions.
export const setCurriculumTags = (documentId: DocumentId, tags: CurriculumTags) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const request = yield* HttpClientRequest.post("/api/curricula/tags").pipe(
      HttpClientRequest.schemaBodyJson(SetCurriculumTagsArgs)({ documentId, tags }),
    );
    const response = yield* client.execute(request);
    if (response.status >= 400) {
      const message = yield* failureMessage(response);
      return yield* Effect.fail(
        new CreateRequestFailed({
          status: response.status,
          message: message ?? `The tags could not be set (HTTP ${response.status}).`,
        }),
      );
    }
    return yield* HttpClientResponse.schemaBodyJson(SetCurriculumTagsResult)(response);
  }).pipe(Effect.scoped);

// The four lifecycle writes that replaced the status control (the status-axis split), all sharing the
// same tagged-failure surface as the other actions. A 409 carries the server's own message — "phase
// only moves forward", "close the rework before publishing" — so the UI can show the real reason
// rather than a generic failure.
const lifecycleWrite = <A, I, ResultA, ResultI>(
  path: string,
  args: Schema.Schema<A, I>,
  payload: A,
  result: Schema.Schema<ResultA, ResultI>,
  failure: string,
) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const request = yield* HttpClientRequest.post(path).pipe(
      HttpClientRequest.schemaBodyJson(args)(payload),
    );
    const response = yield* client.execute(request);
    if (response.status >= 400) {
      const message = yield* failureMessage(response);
      return yield* Effect.fail(
        new CreateRequestFailed({
          status: response.status,
          message: message ?? `${failure} (HTTP ${response.status}).`,
        }),
      );
    }
    return yield* HttpClientResponse.schemaBodyJson(result)(response);
  }).pipe(Effect.scoped);

// Which document this is for its level. A promotion names the incumbent it replaces.
export const setCurriculumRole = (
  documentId: DocumentId,
  role: CurriculumRole,
  supersedes?: DocumentId,
) =>
  lifecycleWrite(
    "/api/curricula/role",
    SetCurriculumRoleArgs,
    { documentId, role, ...(supersedes !== undefined ? { supersedes } : {}) },
    SetCurriculumRoleResult,
    "The role could not be set",
  );

// Move the document forward through production. Refused (409) if it would not advance.
export const advanceCurriculumPhase = (documentId: DocumentId, phase: CurriculumPhase) =>
  lifecycleWrite(
    "/api/curricula/phase",
    AdvanceCurriculumPhaseArgs,
    { documentId, phase },
    AdvanceCurriculumPhaseResult,
    "The phase could not be advanced",
  );

// Pin the current head as what learners see. The server resolves head, so no version is sent.
export const publishCurriculum = (documentId: DocumentId) =>
  lifecycleWrite(
    "/api/curricula/publish",
    PublishCurriculumArgs,
    { documentId },
    PublishCurriculumResult,
    "The curriculum could not be published",
  );

export const openCurriculumRework = (documentId: DocumentId, phase: CurriculumPhase) =>
  lifecycleWrite(
    "/api/curricula/rework",
    OpenCurriculumReworkArgs,
    { documentId, phase },
    CurriculumReworkResult,
    "The rework could not be opened",
  );

export const closeCurriculumRework = (documentId: DocumentId) =>
  lifecycleWrite(
    "/api/curricula/rework/close",
    PublishCurriculumArgs,
    { documentId },
    CurriculumReworkResult,
    "The rework could not be closed",
  );
